import { forwardRef } from "react";
import dayjs from "dayjs";
import { getClientName } from "~/utils";

type ExpoPosition = {
  positionName: string;
  quantity: number;
  duration: number;
  regularHours: number;
  otHours: number;
  hourlyRate: number;
  timeIn: string;
  timeOut: string;
  subtotal: number;
  totalHours: number;
};

type Props = {
  invoiceData: {
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    booking: {
      referenceNumber?: string;
      project?: { name?: string };
    };
    client: {
      companyName?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      contactPhone?: string;
      address?: any;
    };
    services: Array<{
      serial: string;
      serviceType: string;
      serviceDatetime: string;
      client?: {
        companyName?: string;
        firstName?: string;
        lastName?: string;
      };
      location?: {
        city?: string;
        state?: string;
      };
      scope?: string;
      duration?: number;
      travelTime?: number;
      addOnServices?: string[];
      supplies?: string[];
      packageCount: number;
      originalCost: number;
      finalCost: number;
      isOverridden: boolean;
      // Expo data
      isExpoDay?: boolean;
      dayLabel?: string | null;
      expoPositions?: ExpoPosition[] | null;
    }>;
    adminCharges?: Array<{
      name: string;
      rate: number;
      unit: string;
      quantity: number;
      total: number;
    }>;
    additionalCharges?: Array<{
      description: string;
      notes?: string;
      date?: string;
      quantity: number;
      rate: number;
      total: number;
      type: 'custom' | 'supply';
    }>;
    storage?: {
      locationName: string;
      itemCount: number;
      totalCubicFeet: number;
      billableCubicFeet: number;
      minimumCubicFeet: number;
      rate: number;
      total: number;
    } | null;
    financial: {
      subtotal: number;
      depositAmount: number;
      balanceDue: number;
      netTerms: number;
      paymentTerms: string;
    };
  };
  mode?: 'invoice' | 'estimate';
  paymentLinkUrl?: string;
};

const formatCurrency = (amount: number): string => {
  return `$${Number(amount ?? 0)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
};

// Format time for display (convert 24h to 12h)
const formatTime = (time: string): string => {
  if (!time) return '-';
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
};

export const InvoicePrintContent = forwardRef<HTMLDivElement, Props>(
  ({ invoiceData, mode = 'invoice', paymentLinkUrl }, componentRef) => {
    const {
      invoiceNumber,
      invoiceDate,
      dueDate,
      booking,
      client,
      services,
      adminCharges,
      additionalCharges,
      storage,
      financial,
    } = invoiceData;

    const isEstimate = mode === 'estimate';
    const docType = isEstimate ? 'Estimate' : 'Invoice';
    const docTypeUpper = isEstimate ? 'ESTIMATE' : 'INVOICE';

    return (
      <>
        <div className="printable-invoice-content">
          <div ref={componentRef} className="invoice-container">
            {/* Invoice Header */}
            <div className="invoice-header">
              <div className="company-info">
                <h1>ART HANDLER COLLECTIVE</h1>
                <p>15 Cumberland Street</p>
                <p>Brooklyn, NY 11206</p>
                <p>Phone: (646) 741-4047</p>
                <p>Email: info@arthandlercollective.org</p>
              </div>
              <div className="invoice-meta">
                <h2>{docTypeUpper}</h2>
                <p><strong>{docType} #:</strong> {isEstimate ? `EST${booking?.referenceNumber}` : invoiceNumber}</p>
                <p><strong>Date:</strong> {dayjs(invoiceDate).format("MM/DD/YYYY")}</p>
                {!isEstimate && <p><strong>Due Date:</strong> {dueDate === 'Before Delivery' ? dueDate : dayjs(dueDate).format("MM/DD/YYYY")}</p>}
                {isEstimate && <p><strong>Payment Terms:</strong> {financial.paymentTerms}</p>}
                {booking?.referenceNumber && (
                  <p><strong>Booking:</strong> {booking.referenceNumber}</p>
                )}
                {booking?.project?.name && (
                  <p><strong>Project:</strong> {booking.project.name}</p>
                )}
              </div>
            </div>

            {/* Bill To Section */}
            <div className="bill-to-section">
              <h3>BILL TO:</h3>
              <div className="client-details">
                <p className="client-name">
                  {client.companyName || getClientName(client as any)}
                </p>
                {client.address && (
                  <>
                    <p>{client.address.address}</p>
                    {client.address.address2 && <p>{client.address.address2}</p>}
                    <p>
                      {client.address.city}, {client.address.state} {client.address.zip}
                    </p>
                  </>
                )}
                {client.email && <p>Email: {client.email}</p>}
                {client.contactPhone && <p>Phone: {client.contactPhone}</p>}
              </div>
            </div>

            {/* Service Line Items */}
            <div className="line-items-section">
              <table className="line-items-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Location</th>
                    <th>Description</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((service, index) => (
                    <>
                      <tr key={index}>
                        <td>
                          <div className="service-name">
                            {service.isExpoDay && service.dayLabel ? (
                              <span>{service.dayLabel}</span>
                            ) : (
                              service.serviceType
                            )}
                          </div>
                          <div className="service-serial">#{service.serial}</div>
                          {service.isExpoDay && <div className="expo-tag">EXPO</div>}
                        </td>
                        <td>{dayjs(service.serviceDatetime).format("MM/DD/YYYY")}</td>
                        <td>
                          {service.client?.companyName || getClientName(service.client as any) || "N/A"}
                        </td>
                        <td>
                          {service.location?.city || service.location?.state
                            ? `${service.location?.city || ''}${service.location?.city && service.location?.state ? ', ' : ''}${service.location?.state || ''}`
                            : "TBD"}
                        </td>
                        <td>
                          <div className="service-description">
                            {service.scope && (
                              <div className="scope-text">{service.scope}</div>
                            )}
                            {service.packageCount > 0 && (
                              <div className="detail-text">
                                {service.packageCount} package{service.packageCount > 1 ? "s" : ""}
                              </div>
                            )}
                            {service.duration && !service.isExpoDay && (
                              <div className="detail-text">
                                Site time: {service.duration} hr{service.duration !== 1 ? "s" : ""}
                              </div>
                            )}
                            {service.addOnServices && service.addOnServices.length > 0 && (
                              <div className="detail-text">
                                Add-ons: {service.addOnServices.join(", ")}
                              </div>
                            )}
                            {service.supplies && service.supplies.length > 0 && (
                              <div className="detail-text">
                                Supplies: {service.supplies.join(", ")}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="text-right amount-cell">
                          {formatCurrency(service.finalCost)}
                        </td>
                      </tr>
                      {/* Expo Positions Breakdown */}
                      {service.isExpoDay && service.expoPositions && service.expoPositions.length > 0 && (
                        <>
                          <tr className="expo-positions-header" key={`${index}-expo-header`}>
                            <td colSpan={6}>
                              <div className="expo-positions-title">Labor Breakdown</div>
                            </td>
                          </tr>
                          <tr className="expo-positions-table-header" key={`${index}-expo-cols`}>
                            <td className="expo-col-header">Position</td>
                            <td className="expo-col-header text-center">Staff</td>
                            <td className="expo-col-header text-center">Reg Hrs</td>
                            <td className="expo-col-header text-center">OT Hrs</td>
                            <td className="expo-col-header text-center">Total Hrs</td>
                            <td className="expo-col-header text-right">Amount</td>
                          </tr>
                          {service.expoPositions.map((pos, posIdx) => {
                            const regularCost = pos.quantity * pos.regularHours * pos.hourlyRate;
                            const otCost = pos.quantity * pos.otHours * pos.hourlyRate * 1.5;
                            return (
                              <tr className="expo-position-row" key={`${index}-pos-${posIdx}`}>
                                <td className="expo-position-name">
                                  <div>{pos.positionName}</div>
                                  <div className="rate-detail">
                                    {formatCurrency(pos.hourlyRate)}/hr
                                    {pos.otHours > 0 && <span className="ot-rate"> • OT: {formatCurrency(pos.hourlyRate * 1.5)}/hr</span>}
                                  </div>
                                </td>
                                <td className="text-center">{pos.quantity}</td>
                                <td className="text-center">{pos.regularHours}h</td>
                                <td className="text-center">
                                  {pos.otHours > 0 ? <span className="ot-badge">{pos.otHours}h</span> : '-'}
                                </td>
                                <td className="text-center">{pos.totalHours}h</td>
                                <td className="text-right">
                                  <div>{formatCurrency(pos.subtotal)}</div>
                                  {pos.otHours > 0 && (
                                    <div className="rate-detail">
                                      {formatCurrency(regularCost)} + <span className="ot-rate">{formatCurrency(otCost)}</span>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          <tr className="expo-positions-total" key={`${index}-expo-total`}>
                            <td className="expo-total-label">
                              Total: {service.expoPositions.reduce((sum, p) => sum + p.quantity, 0)} staff
                            </td>
                            <td></td>
                            <td className="text-center">
                              {service.expoPositions.reduce((sum, p) => sum + p.regularHours, 0)}h
                            </td>
                            <td className="text-center ot-badge">
                              {(() => {
                                const totalOt = service.expoPositions.reduce((sum, p) => sum + p.otHours, 0);
                                return totalOt > 0 ? `${totalOt}h` : '-';
                              })()}
                            </td>
                            <td className="text-center font-bold">
                              {service.expoPositions.reduce((sum, p) => sum + p.totalHours, 0)}h
                            </td>
                            <td className="text-right font-bold">
                              {formatCurrency(service.expoPositions.reduce((sum, p) => sum + p.subtotal, 0))}
                            </td>
                          </tr>
                        </>
                      )}
                    </>
                  ))}

                  {/* Additional Charges */}
                  {additionalCharges && additionalCharges.length > 0 && (
                    <>
                      <tr className="section-divider">
                        <td colSpan={6}><strong>Additional Charges</strong></td>
                      </tr>
                      {additionalCharges.map((charge, index) => (
                        <tr key={`additional-${index}`}>
                          <td>
                            <div className="service-name">{charge.description}</div>
                            <div className="service-serial">{charge.type === 'supply' ? 'Supply' : 'Custom'}</div>
                          </td>
                          <td>{charge.date ? dayjs(charge.date).format("MM/DD/YYYY") : '-'}</td>
                          <td></td>
                          <td></td>
                          <td>
                            {charge.notes && (
                              <div className="scope-text">{charge.notes}</div>
                            )}
                            <div className="detail-text">
                              {formatCurrency(charge.rate)} × {charge.quantity}
                            </div>
                          </td>
                          <td className="text-right amount-cell">
                            {formatCurrency(charge.total)}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}

                  {/* Admin Charges */}
                  {adminCharges && adminCharges.length > 0 && (
                    <>
                      <tr className="section-divider">
                        <td colSpan={6}><strong>Administrative Charges</strong></td>
                      </tr>
                      {adminCharges.map((charge, index) => (
                        <tr key={`charge-${index}`}>
                          <td colSpan={4}>{charge.name}</td>
                          <td className="text-center">
                            {formatCurrency(charge.rate)} / {charge.unit} × {charge.quantity}
                          </td>
                          <td className="text-right amount-cell">
                            {formatCurrency(charge.total)}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}

                  {/* Storage */}
                  {storage && (
                    <>
                      <tr className="section-divider">
                        <td colSpan={6}><strong>Storage</strong></td>
                      </tr>
                      <tr>
                        <td colSpan={2}>
                          <div className="service-name">Monthly Storage — {storage.locationName}</div>
                          <div className="service-serial">
                            {storage.itemCount} item{storage.itemCount !== 1 ? 's' : ''} · {storage.totalCubicFeet.toFixed(2)} ft³
                            {storage.billableCubicFeet > storage.totalCubicFeet && ` (${storage.minimumCubicFeet} ft³ min)`}
                          </div>
                        </td>
                        <td></td>
                        <td></td>
                        <td>
                          <div className="detail-text">
                            {storage.billableCubicFeet.toFixed(2)} ft³ × {formatCurrency(storage.rate)}/ft³
                          </div>
                        </td>
                        <td className="text-right amount-cell">
                          {formatCurrency(storage.total)}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals Section */}
            <div className="totals-section">
              <div className="totals-grid">
                <div className="total-row">
                  <span className="total-label">Subtotal:</span>
                  <span className="total-amount">{formatCurrency(financial.subtotal)}</span>
                </div>

                {financial.depositAmount > 0 && (
                  <>
                    <div className="total-row deposit-row">
                      <span className="total-label">Deposit (Due Immediately):</span>
                      <span className="total-amount deposit-amount">
                        -{formatCurrency(financial.depositAmount)}
                      </span>
                    </div>
                    <div className="total-row balance-row">
                      <span className="total-label">Balance Due:</span>
                      <span className="total-amount balance-amount">
                        {formatCurrency(financial.balanceDue)}
                      </span>
                    </div>
                  </>
                )}

                <div className="total-row grand-total-row">
                  <span className="total-label">
                    {financial.depositAmount > 0 ? "Total Invoice Amount:" : "Amount Due:"}
                  </span>
                  <span className="total-amount grand-total-amount">
                    {formatCurrency(financial.subtotal)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Terms */}
            <div className="payment-terms-section">
              <h3>{isEstimate ? 'TERMS' : 'PAYMENT TERMS'}</h3>
              <p><strong>Payment Terms:</strong> {financial.paymentTerms}</p>
              {!isEstimate && dueDate !== 'Before Delivery' && (
                <p><strong>Due Date:</strong> {dayjs(dueDate).format("MMMM DD, YYYY")}</p>
              )}
              {financial.depositAmount > 0 && (
                <p className="deposit-note">
                  <strong>Note:</strong> A deposit of {formatCurrency(financial.depositAmount)} {isEstimate ? 'may be' : 'is'} required
                  to confirm service.{!isEstimate && ` Remaining balance of ${formatCurrency(financial.balanceDue)} is due by ${dueDate === 'Before Delivery' ? 'delivery' : dayjs(dueDate).format("MMMM DD, YYYY")}.`}
                </p>
              )}
              {isEstimate && (
                <p className="estimate-note">
                  <strong>Note:</strong> This is an estimate only. Final charges may vary based on actual services rendered.
                </p>
              )}
            </div>

            {/* Payment Link */}
            {!isEstimate && paymentLinkUrl && (
              <div className="payment-link-section">
                <h3>PAY ONLINE</h3>
                <p>Pay this invoice securely online:</p>
                <a href={paymentLinkUrl} className="payment-link">{paymentLinkUrl}</a>
              </div>
            )}

            {/* Footer */}
            <div className="invoice-footer">
              <p>Thank you for {isEstimate ? 'considering Art Handler Collective!' : 'your business!'}</p>
              {!isEstimate && (
                <p className="footer-note">
                  Please include invoice number {invoiceNumber} with your payment.
                </p>
              )}
              <p className="footer-note">
                For questions regarding this {isEstimate ? 'estimate' : 'invoice'}, please contact us at info@arthandlercollective.org
              </p>
            </div>
          </div>
        </div>

        <style>
          {`
          .printable-invoice-content {
            position: absolute;
            opacity: 0;
            height: 0;
            overflow: hidden;
          }

          .invoice-container {
            width: 816px; /* Letter width at 96dpi */
            font-family: 'Helvetica', 'Arial', sans-serif;
            font-size: 11px;
            line-height: 1.4;
            color: #333;
            background: white;
            padding: 40px;
            box-sizing: border-box;
          }

          .invoice-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #333;
          }

          .company-info h1 {
            font-size: 24px;
            font-weight: bold;
            margin: 0 0 10px 0;
            color: #000;
          }

          .company-info p {
            margin: 2px 0;
            font-size: 10px;
          }

          .invoice-meta {
            text-align: right;
          }

          .invoice-meta h2 {
            font-size: 28px;
            font-weight: bold;
            margin: 0 0 10px 0;
            color: #000;
          }

          .invoice-meta p {
            margin: 4px 0;
            font-size: 11px;
          }

          .bill-to-section {
            margin-bottom: 30px;
          }

          .bill-to-section h3 {
            font-size: 12px;
            font-weight: bold;
            margin: 0 0 10px 0;
            color: #666;
            letter-spacing: 0.5px;
          }

          .client-details {
            background: #f8f8f8;
            padding: 15px;
            border-left: 3px solid #333;
          }

          .client-name {
            font-size: 13px;
            font-weight: bold;
            margin-bottom: 8px;
          }

          .client-details p {
            margin: 3px 0;
            font-size: 10px;
          }

          .line-items-section {
            margin-bottom: 30px;
          }

          .line-items-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }

          .line-items-table thead {
            background: #333;
            color: white;
          }

          .line-items-table th {
            padding: 10px 8px;
            text-align: left;
            font-size: 10px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .line-items-table th.text-right {
            text-align: right;
          }

          .line-items-table td {
            padding: 12px 8px;
            border-bottom: 1px solid #ddd;
            vertical-align: top;
            font-size: 10px;
          }

          .line-items-table tbody tr:last-child td {
            border-bottom: 2px solid #333;
          }

          .service-name {
            font-weight: bold;
            font-size: 11px;
            margin-bottom: 2px;
          }

          .service-serial {
            color: #666;
            font-size: 9px;
          }

          .expo-tag {
            display: inline-block;
            background: #9333ea;
            color: white;
            font-size: 8px;
            padding: 1px 4px;
            border-radius: 2px;
            margin-top: 2px;
          }

          .expo-positions-header td {
            padding: 8px 8px 4px 20px !important;
            border-bottom: none !important;
          }

          .expo-positions-title {
            font-size: 9px;
            font-weight: bold;
            color: #6b21a8;
            text-transform: uppercase;
            letter-spacing: 0.3px;
          }

          .expo-positions-table-header td {
            padding: 4px 8px !important;
            border-bottom: 1px solid #e9d5ff !important;
            background: #faf5ff;
          }

          .expo-col-header {
            font-size: 8px;
            font-weight: bold;
            color: #7c3aed;
            text-transform: uppercase;
          }

          .expo-position-row td {
            padding: 6px 8px !important;
            border-bottom: 1px solid #f3e8ff !important;
            background: #fdfcfe;
            font-size: 9px;
          }

          .expo-position-name {
            padding-left: 20px !important;
            font-weight: 500;
          }

          .ot-badge {
            color: #ea580c;
            font-size: 8px;
            font-weight: bold;
          }

          .rate-detail {
            font-size: 7px;
            color: #888;
            margin-top: 1px;
          }

          .ot-rate {
            color: #ea580c;
          }

          .expo-positions-total td {
            padding: 6px 8px !important;
            border-bottom: 1px solid #ddd !important;
            background: #f3e8ff;
            font-size: 9px;
          }

          .expo-total-label {
            padding-left: 20px !important;
            font-weight: bold;
            color: #6b21a8;
          }

          .font-bold {
            font-weight: bold;
          }

          .override-indicator {
            color: #ff6b00;
            font-size: 9px;
            font-style: italic;
            margin-top: 2px;
          }

          .service-description {
            font-size: 10px;
          }

          .scope-text {
            margin-bottom: 4px;
            font-weight: 500;
          }

          .detail-text {
            color: #666;
            font-size: 9px;
            margin: 2px 0;
          }

          .amount-cell {
            font-weight: bold;
            font-size: 11px;
          }

          .text-right {
            text-align: right;
          }

          .text-center {
            text-align: center;
          }

          .section-divider td {
            padding-top: 20px !important;
            padding-bottom: 8px !important;
            background: #f0f0f0;
            border-top: 2px solid #999;
          }

          .totals-section {
            margin-bottom: 30px;
            display: flex;
            justify-content: flex-end;
          }

          .totals-grid {
            width: 350px;
            background: #f8f8f8;
            padding: 15px 20px;
            border: 1px solid #ddd;
          }

          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #ddd;
          }

          .total-row:last-child {
            border-bottom: none;
          }

          .total-label {
            font-size: 11px;
            color: #666;
          }

          .total-amount {
            font-size: 11px;
            font-weight: bold;
          }

          .deposit-row {
            background: #fff3cd;
            margin: 0 -20px;
            padding: 8px 20px;
          }

          .deposit-amount {
            color: #856404;
          }

          .balance-row .total-label {
            font-weight: bold;
            color: #333;
          }

          .balance-amount {
            color: #d9534f;
            font-size: 13px;
          }

          .grand-total-row {
            margin-top: 10px;
            padding-top: 15px !important;
            border-top: 2px solid #333 !important;
            border-bottom: none;
          }

          .grand-total-row .total-label {
            font-size: 13px;
            font-weight: bold;
            color: #000;
          }

          .grand-total-amount {
            font-size: 16px;
            font-weight: bold;
            color: #000;
          }

          .payment-terms-section {
            margin-bottom: 30px;
            padding: 15px;
            background: #f8f8f8;
            border: 1px solid #ddd;
          }

          .payment-terms-section h3 {
            font-size: 12px;
            font-weight: bold;
            margin: 0 0 10px 0;
            color: #666;
            letter-spacing: 0.5px;
          }

          .payment-terms-section p {
            margin: 6px 0;
            font-size: 10px;
          }

          .deposit-note {
            margin-top: 10px;
            padding: 10px;
            background: #fff3cd;
            border-left: 3px solid #856404;
            color: #856404;
          }

          .payment-link-section {
            margin-top: 20px;
            padding: 15px;
            background: #f0fdf4;
            border: 1px solid #bbf7d0;
            border-radius: 6px;
          }

          .payment-link-section h3 {
            font-size: 12px;
            font-weight: bold;
            margin-bottom: 5px;
            color: #166534;
          }

          .payment-link-section p {
            font-size: 10px;
            margin-bottom: 5px;
            color: #333;
          }

          .payment-link-section .payment-link {
            font-size: 10px;
            color: #2563eb;
            word-break: break-all;
          }

          .invoice-footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #ddd;
          }

          .invoice-footer p {
            margin: 6px 0;
            font-size: 11px;
          }

          .invoice-footer .footer-note {
            font-size: 9px;
            color: #666;
          }

          @media print {
            @page {
              size: letter;
              margin: 0.75in 0.5in;
            }

            .printable-invoice-content {
              position: static;
              opacity: 1;
              height: auto;
              overflow: visible;
            }

            .invoice-container {
              width: 100%;
              padding: 0 30px;
              box-sizing: border-box;
            }

            .line-items-table {
              page-break-inside: avoid;
            }

            .totals-section {
              page-break-inside: avoid;
            }

            .payment-terms-section {
              page-break-inside: avoid;
            }
          }
        `}
        </style>
      </>
    );
  }
);

InvoicePrintContent.displayName = "InvoicePrintContent";
